import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import {
  DEFAULT_TIME_RANGE,
  readStoredTimeRange,
  readUrlTimeRange,
  selectionPreset,
  selectionToDateFilters,
  subscribeTimeRange,
  writeStoredTimeRange,
} from '../utils/timeRange';

/**
 * React access to the global time range (the model is in utils/timeRange).
 *
 *   useGlobalTimeRange(fallback)   'last_30_days' etc. — the URL override, else
 *                                  the stored selection, else `fallback`.
 *                                  Preset-only: a custom range maps to the
 *                                  smallest covering preset.
 *   useGlobalDateFilters(fallback) { time_range, start_date, end_date } — the
 *                                  same, but keeps a custom window (FinOps).
 *   useStoredTimeRange()           the persisted selection object, or null.
 *   useUrlTimeRangeOverride(src)   the URL's override, or null; written
 *                                  through to storage on arrival.
 *
 * All re-render when the selection changes, in this tab or another one.
 */

// Serialized so useSyncExternalStore sees an identical value between changes.
const storedSnapshot = () => {
  const selection = readStoredTimeRange();
  return selection ? JSON.stringify(selection) : '';
};
const serverSnapshot = () => '';

const parse = (serialized) => (serialized ? JSON.parse(serialized) : null);

export function useStoredTimeRange() {
  const serialized = useSyncExternalStore(subscribeTimeRange, storedSnapshot, serverSnapshot);
  return useMemo(() => parse(serialized), [serialized]);
}

/**
 * The range the URL explicitly asks for (shared link, bookmark, drill-down),
 * or null. The caller honours it for this view; it is also written through to
 * storage so it becomes the selection everywhere else.
 */
export function useUrlTimeRangeOverride(source) {
  const search = source == null
    ? (typeof window === 'undefined' ? '' : window.location.search) : String(source);
  const override = useMemo(() => readUrlTimeRange(search), [search]);
  const serialized = override ? JSON.stringify(override) : '';
  useEffect(() => {
    if (serialized) writeStoredTimeRange(JSON.parse(serialized));
  }, [serialized]);
  return useMemo(() => parse(serialized), [serialized]);
}

function useEffectiveSelection() {
  const { search } = useLocation();
  const override = useUrlTimeRangeOverride(search);
  const stored = useStoredTimeRange();
  return override || stored;
}

export default function useGlobalTimeRange(fallback = DEFAULT_TIME_RANGE) {
  return selectionPreset(useEffectiveSelection()) || fallback;
}

export function useGlobalDateFilters(fallback = DEFAULT_TIME_RANGE) {
  const selection = useEffectiveSelection();
  return useMemo(() => selectionToDateFilters(selection, fallback), [selection, fallback]);
}
